/**
 * Mobile numbers and synthetic sign-in emails. CANONICAL COPY.
 *
 * Pure TypeScript, no Deno or Node APIs and no imports, so the same file is used by the
 * Edge Functions (Deno) and re-exported to the apps by `packages/shared/src/phone.ts`.
 * The CRM, the member app and `create-member` must build the SAME address from the same
 * typed number, or a phone-only member can never sign in. Tests:
 * `packages/shared/src/phone.test.ts`.
 *
 * Rules (CLAUDE.md "Sign-in"):
 *   - Every member has a Pakistani mobile number, stored in E.164: +923001234567.
 *   - A member without email signs in with mobile + password. Their auth account's email
 *     is synthetic: 923001234567@phone.<app domain>. It is never shown and never emailed.
 */

/**
 * Turns what a person types into E.164, or `null` if it is not a Pakistani mobile.
 *
 *   "0300-1234567"      → "+923001234567"
 *   "+92 300 1234567"   → "+923001234567"
 *   "923001234567"      → "+923001234567"
 *   "0092 300 1234567"  → "+923001234567"
 *   "300 1234567"       → "+923001234567"
 *
 * A Pakistani mobile is 10 digits after the country code and starts with 3 (3XX XXXXXXX).
 */
export function normalizePakistaniMobile(input: string | null | undefined): string | null {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (trimmed === '') return null;
  // Only digits, spaces, dashes, dots, brackets and one leading plus are allowed.
  if (!/^\+?[\d\s\-().]+$/.test(trimmed)) return null;

  let digits = trimmed.replace(/\D/g, '');
  if (digits.startsWith('0092')) digits = digits.slice(4);
  else if (digits.startsWith('92') && digits.length === 12) digits = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);

  if (!/^3\d{9}$/.test(digits)) return null;
  return `+92${digits}`;
}

/** The domain part used for synthetic addresses: `phone.<app domain>`. */
function phoneDomain(appDomain: string): string {
  const domain = appDomain.trim().toLowerCase().replace(/^@/, '').replace(/^phone\./, '');
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain)) {
    throw new Error(`Invalid app domain for synthetic sign-in emails: "${appDomain}"`);
  }
  return `phone.${domain}`;
}

/**
 * The hidden sign-in email of a phone-only member:
 *   syntheticEmail("+923001234567", "gymcoach.pk") → "923001234567@phone.gymcoach.pk"
 *
 * Accepts any format `normalizePakistaniMobile` accepts. Throws on an invalid number,
 * because silently building a wrong address locks the member out.
 */
export function syntheticEmail(mobile: string, appDomain: string): string {
  const e164 = normalizePakistaniMobile(mobile);
  if (!e164) throw new Error('Enter a valid Pakistani mobile number, like 0300 1234567');
  return `${e164.slice(1)}@${phoneDomain(appDomain)}`;
}

/** True for an address built by `syntheticEmail`. Such an address must never be emailed. */
export function isSyntheticEmail(email: string | null | undefined, appDomain?: string): boolean {
  if (typeof email !== 'string') return false;
  const lower = email.trim().toLowerCase();
  if (appDomain) return lower.endsWith(`@${phoneDomain(appDomain)}`) && /^\d+@/.test(lower);
  return /^\d{8,15}@phone\./.test(lower);
}
